import { useQuery, useMutation } from '@tanstack/react-query';
import { supabase } from '@/lib/supabaseClient';
import { toast } from 'sonner';

export interface ReferralStats {
  isAffiliate: boolean;
  hasPaidAffiliateAccess: boolean;
  referralCode: string | null;
  promoCode: string | null;
  rewardfulToken: string | null;
  referredUsers: number;
  totalEarned: number;
  pendingEarnings: number;
  paidEarnings: number;
  commission: {
    type: 'percent' | 'amount';
    value?: number;
    cents?: number;
    currency?: string;
  } | null;
  discountPercent: number;
  referrals: Array<{
    id: string;
    referred_user_id: string | null;
    status: 'pending' | 'converted' | 'expired';
    conversion_date: string | null;
    created_at: string;
  }>;
  earnings: Array<{
    id: string;
    amount_cents: number;
    status: 'pending' | 'paid' | 'failed';
    created_at: string;
    payout_date: string | null;
  }>;
  links: {
    referralLink: string | null;
    shareLinks: {
      email: string | null;
      twitter: string | null;
    };
  };
}

export function useReferralStats() {
  return useQuery<ReferralStats>({
    queryKey: ['referral-stats'],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/rewardful-affiliate-stats`,
        {
          headers: {
            Authorization: `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to fetch referral stats');
      }

      return response.json();
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
    refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
  });
}

export function useValidatePromoCode() {
  return useMutation({
    mutationFn: async (promoCode: string) => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/validate-promo-code`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ promoCode }),
        }
      );

      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.error || 'Failed to validate promo code');
      }

      return result;
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
}

export function useGetAffiliateByPromoCode() {
  return useMutation({
    mutationFn: async (promoCode: string) => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-affiliate-by-promo-code`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ promoCode }),
        }
      );

      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.error || 'Failed to get affiliate data');
      }

      return result;
    },
  });
}

// Hook to get referral earnings summary
export function useReferralEarnings() {
  return useQuery({
    queryKey: ['referral-earnings'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('referral_earnings')
        .select('*')
        .eq('referrer_user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      return data || [];
    },
  });
}

// Hook to get user's referrals
export function useUserReferrals() {
  return useQuery({
    queryKey: ['user-referrals'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('referrals')
        .select('*')
        .eq('referrer_user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      return data || [];
    },
  });
}